import type { ScenarioStateResponse } from "@/api/types";

/**
 * 동료별 누적 "함께한 순간" 메모.
 *
 * <p>2026-04-24: 사용자 피드백 "동료 사건 결과를 원정 인원 패널 쪽 메모로 더 축적" 대응.
 * 선택 이벤트가 해소될 때 {@code outcomeTags} 에 남기는 태그를 기반으로, 해당 동료의
 * 여정에 한 줄씩 쌓이는 메모를 재구성한다.
 *
 * <p>데이터 흐름:
 * <ol>
 *   <li>동료 이벤트 선택 확정 → 서버가 {@code scenarioState.outcomeTags} 에 태그 추가
 *       (예: "varan-keeps-the-line")</li>
 *   <li>클라이언트에서 outcomeTags 를 훑어, 태그 → (companionId, 회상 문구) 매핑</li>
 *   <li>SituationDrawer CompanionRow 확장 영역에 해당 동료 메모 스택 표시</li>
 * </ol>
 *
 * <p>서버 측 추가 저장 없음. 순수 derivation. 태그 추가 시 이 테이블만 확장하면 됨.
 *
 * <p>콘텐츠 팀 확장 가이드: 동료 이벤트가 JSON 에 추가될 때마다 해당 이벤트의 각
 * choice 에서 뿌리는 outcomeTag 를 여기 {@link COMPANION_MEMORY_TABLE} 에 등록.
 */

export interface CompanionMemory {
    /** 대상 동료 id (ScenarioCompanionSource.id 와 매치). */
    companionId: string;
    /** 원천 outcomeTag — dedupe / 재구성 키. */
    tag: string;
    /** 한 줄 회상. 1~2 문장. 메모장 톤. */
    line: string;
    /** 톤 힌트 — "warm" / "cold" / "solemn" / "steady". UI 색상 결정. */
    tone: "warm" | "cold" | "solemn" | "steady";
}

/**
 * outcomeTag → CompanionMemory 매핑. 키는 시나리오 source-of-truth 의 태그와 일치해야 함.
 *
 * <p>frontier-vanguard 동료 이벤트: 1차 5건 (minAffinity 0) + 2차 5건 (minAffinity 2)
 * = 10 이벤트 × 2 choice = <strong>20 outcomeTags</strong>.
 *
 * <p>2026-04-24 사용자 콘텐츠 확정 태그 + resolutionText 결을 그대로 반영.
 */
const COMPANION_MEMORY_TABLE: Record<string, Omit<CompanionMemory, "tag">> = {
    // ── 1차 (minAffinity: 0) ─────────────────────────────────────

    // 부관 바란 — line-of-return
    "varan-keeps-the-line": {
        companionId: "frontier-vanguard/adjutant-varan",
        line: "바란과 함께 전진선보다 귀환선을 먼저 다시 그었다. 그날 이후 몇몇 병사는 지휘막을 조금 덜 차갑게 보기 시작했다.",
        tone: "steady",
    },
    "varan-marches-hard": {
        companionId: "frontier-vanguard/adjutant-varan",
        line: "끝까지 밀어붙이라 말했고, 바란은 반박 없이 따랐다. 이후 그의 보고서 끝에는 손실 추정이 한 줄씩 더 길어진다.",
        tone: "cold",
    },

    // 보급장교 헤스타 — count-the-sacks
    "hesta-opens-the-ledger": {
        companionId: "frontier-vanguard/quartermaster-hesta",
        line: "헤스타에게 장부를 있는 그대로 읽게 했다. 막사는 무거워졌지만, 그 밤 이후 누구도 지휘막이 거짓으로 밤을 넘겼다고 말하지 못한다.",
        tone: "warm",
    },
    "hesta-borrows-tomorrow": {
        companionId: "frontier-vanguard/quartermaster-hesta",
        line: "헤스타는 장부 마지막 칸을 비워 두고 입술을 깨물었다. 오늘 밤의 숨은 이어졌지만, 그 빈칸은 다시 돌아올 것이다.",
        tone: "cold",
    },

    // 정찰병 이리야 — echo-before-dawn
    "iriya-follows-the-echo": {
        companionId: "frontier-vanguard/scout-iriya",
        line: "이리야의 귀를 믿고 행군을 하루 미뤘다. 보이지 않던 균열 하나를 밟지 않고 지나갈 수 있었다.",
        tone: "steady",
    },
    "iriya-cuts-the-route": {
        companionId: "frontier-vanguard/scout-iriya",
        line: "이리야에게 더 묻지 않고 우회 경로를 긋게 했다. 정면의 함정은 피했지만, 그 길엔 더 긴 피로가 따라붙는다.",
        tone: "cold",
    },

    // 깃발수 다간 — hold-the-cloth
    "dagan-raises-the-banner": {
        companionId: "frontier-vanguard/standard-bearer-dagan",
        line: "다간에게 마지막까지 깃발을 세우라 말했다. 위험은 커졌지만, 원정이 꺾이지 않았다는 믿음이 조금 더 오래 남는다.",
        tone: "warm",
    },
    "dagan-hides-the-colors": {
        companionId: "frontier-vanguard/standard-bearer-dagan",
        line: "다간은 잠시 망설이다가 천을 품 안으로 접어 넣었다. 후방은 쓸데없는 표적 하나를 줄인 채 그 밤을 넘겼다.",
        tone: "steady",
    },

    // 의무관 미렌 — count-the-breathing
    "miren-opens-the-tent": {
        companionId: "frontier-vanguard/surgeon-miren",
        line: "미렌에게 의무막 문을 닫지 말라고 했다. 밤은 더 길어졌지만, 누군가는 자신이 장부 바깥으로 밀려나지 않았다고 느낀다.",
        tone: "warm",
    },
    "miren-keeps-the-order": {
        companionId: "frontier-vanguard/surgeon-miren",
        line: "미렌은 대꾸 없이 명부 순서를 다시 바꿨다. 더 많은 목숨을 붙들 수는 있었지만, 문밖의 침묵은 한층 차갑게 식었다.",
        tone: "cold",
    },

    // ── 2차 (minAffinity: 2) ─────────────────────────────────────

    // 부관 바란 — count-the-absent
    "varan-reads-the-roll": {
        companionId: "frontier-vanguard/adjutant-varan",
        line: "바란에게 빈자리까지 끝까지 이름을 읽으라 했다. 밤은 더 무거워졌지만, 그 자리들은 숫자가 아니라 이름으로 남았다.",
        tone: "solemn",
    },
    "varan-burns-the-page": {
        companionId: "frontier-vanguard/adjutant-varan",
        line: "바란은 묻지 않고 명부를 덮었다. 일은 빨라졌지만, 지휘막 안에서 어떤 이름들은 한 줄의 숫자로만 남게 된다.",
        tone: "cold",
    },

    // 보급장교 헤스타 — ledger-of-debt
    "hesta-calls-the-debt": {
        companionId: "frontier-vanguard/quartermaster-hesta",
        line: "헤스타에게 장부를 숨기지 말라 했다. 원망은 잠시 커졌지만, 그 청구서는 뒤에서 칼날처럼 돌아오지 못하게 된다.",
        tone: "warm",
    },
    "hesta-hides-the-rate": {
        companionId: "frontier-vanguard/quartermaster-hesta",
        line: "헤스타는 말없이 장부를 덮어 두었다. 오늘은 넘어갔지만, 그 값은 더 늦고 더 깊은 밤에 사람들을 찾아올 것이다.",
        tone: "cold",
    },

    // 정찰병 이리야 — name-the-ridge
    "iriya-names-the-ridge": {
        companionId: "frontier-vanguard/scout-iriya",
        line: "이리야가 붙인 이름을 보고서에 그대로 남겼다. 별것 아닌 한 줄이지만, 그날 이후 몇몇 길은 처음으로 누군가의 기억을 얻는다.",
        tone: "warm",
    },
    "iriya-erases-the-mark": {
        companionId: "frontier-vanguard/scout-iriya",
        line: "이리야는 말없이 낯선 이름을 선으로 지워 냈다. 지도는 단정해졌지만, 길은 다시 사람보다 명령에만 속하게 된다.",
        tone: "cold",
    },

    // 깃발수 다간 — who-sees-the-banner
    "dagan-shows-the-scars": {
        companionId: "frontier-vanguard/standard-bearer-dagan",
        line: "깃발의 얼룩까지 감추지 말라 했다. 깃발은 덜 아름다워졌지만, 사람들은 자신들의 버팀이 닦여 나가진 않았다고 느낀다.",
        tone: "solemn",
    },
    "dagan-folds-the-cloth-close": {
        companionId: "frontier-vanguard/standard-bearer-dagan",
        line: "다간은 더 깨끗한 면을 바깥으로 접어 올렸다. 대오는 단정해 보이지만, 그 깃발 아래 몇몇은 무엇이 접혀 들어갔는지 안다.",
        tone: "cold",
    },

    // 의무관 미렌 — keep-the-lamp
    "miren-keeps-the-lamp": {
        companionId: "frontier-vanguard/surgeon-miren",
        line: "미렌에게 오늘 밤만큼은 등을 끄지 말라 했다. 의무막의 불빛이 한밤의 체념 몇 개를 늦춰 놓는다.",
        tone: "warm",
    },
    "miren-cuts-the-wick": {
        companionId: "frontier-vanguard/surgeon-miren",
        line: "미렌은 고개를 끄덕이고 심지를 짧게 잘랐다. 누군가는 더 버티겠지만, 또 다른 누군가는 문밖의 어둠 속에서 이름을 잃는다.",
        tone: "solemn",
    },
};

/**
 * 현재 scenarioState.outcomeTags 에서 동료별 메모를 재구성.
 *
 * @returns companionId 기준으로 묶인 Map. 각 값은 추가된 순서대로 메모 배열.
 *   (outcomeTags 는 LinkedHashSet 이라 서버 삽입 순서 유지 — choice 시점 순.)
 */
export function deriveCompanionMemories(
    scenarioState: ScenarioStateResponse | null | undefined,
): Map<string, CompanionMemory[]> {
    const byCompanion = new Map<string, CompanionMemory[]>();
    if (!scenarioState) return byCompanion;
    const tags = scenarioState.outcomeTags ?? [];
    for (const tag of tags) {
        const entry = COMPANION_MEMORY_TABLE[tag];
        if (!entry) continue;
        const memory: CompanionMemory = { tag, ...entry };
        const list = byCompanion.get(entry.companionId) ?? [];
        list.push(memory);
        byCompanion.set(entry.companionId, list);
    }
    return byCompanion;
}

/**
 * 특정 동료의 메모 리스트만 꺼내는 편의 함수.
 */
export function memoriesForCompanion(
    scenarioState: ScenarioStateResponse | null | undefined,
    companionId: string,
): CompanionMemory[] {
    return deriveCompanionMemories(scenarioState).get(companionId) ?? [];
}
